import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { LoaderComponent } from "../../../Common/LoaderComponent";
import { Calendar } from "primereact/calendar";

const ExpenseCreate = () => {
  const navigate = useNavigate();
  const [expenseEntries, setExpenseEntries] = useState([
    {
      date: new Date(),
      expense_category_id: "",
      amount: "",
      bank_id: "",
      reference: "",
      description: "",
    },
  ]);
  const [error, setError] = useState({});
  const [categories, setCategories] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bankBalances, setBankBalances] = useState({});
  const [loadingBalances, setLoadingBalances] = useState({});
  const [balanceErrors, setBalanceErrors] = useState({});

  useEffect(() => {
    Promise.all([
      Http.get("/accounts/expense-categories"),
      Http.get("/accounts/banks"),
    ])
      .then(([categoriesRes, banksRes]) => {
        setCategories(categoriesRes.data);
        setBanks(banksRes.data);
      })
      .catch((error) => {
        console.error("Fetch error:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Add new expense entry
  const addExpenseEntry = () => {
    setExpenseEntries((prev) => [
      ...prev,
      {
        date: new Date(),
        expense_category_id: "",
        amount: "",
        bank_id: "",
        reference: "",
        description: "",
      },
    ]);
  };

  // Remove expense entry
  const removeExpenseEntry = (index) => {
    if (expenseEntries.length > 1) {
      setExpenseEntries((prev) => prev.filter((_, i) => i !== index));
      
      // Clean up related state
      setBankBalances((prev) => {
        const newBalances = { ...prev };
        delete newBalances[index];
        return newBalances;
      });
      setLoadingBalances((prev) => {
        const newLoading = { ...prev };
        delete newLoading[index];
        return newLoading;
      });
      setBalanceErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }
  };

  const fetchBankBalance = (bankId, entryIndex) => {
    if (!bankId) {
      setBankBalances((prev) => ({ ...prev, [entryIndex]: null }));
      setBalanceErrors((prev) => ({ ...prev, [entryIndex]: "" }));
      return;
    }

    setLoadingBalances((prev) => ({ ...prev, [entryIndex]: true }));
    setBalanceErrors((prev) => ({ ...prev, [entryIndex]: "" }));
    Http.get(`/accounts/get-single-bank-ledger-summary/${bankId}`)
      .then((res) => {
        if (res.data.success) {
          setBankBalances((prev) => ({ ...prev, [entryIndex]: res.data.data }));
        } else {
          setBalanceErrors((prev) => ({ ...prev, [entryIndex]: "Failed to fetch bank balance" }));
        }
      })
      .catch((error) => {
        console.error("Balance fetch error:", error);
        setBalanceErrors((prev) => ({ ...prev, [entryIndex]: "Error fetching bank balance" }));
        setBankBalances((prev) => ({ ...prev, [entryIndex]: null }));
      })
      .finally(() => {
        setLoadingBalances((prev) => ({ ...prev, [entryIndex]: false }));
      });
  };

  const handleChange = (e, entryIndex) => {
    const name = e.target.name;
    const value = e.target.value;
    
    setExpenseEntries((prev) =>
      prev.map((entry, index) =>
        index === entryIndex ? { ...entry, [name]: value } : entry
      )
    );

    // Fetch bank balance when bank is selected
    if (name === "bank_id") {
      fetchBankBalance(value, entryIndex);
    }

    // BALANCE CHECK LOGIC ---
    if (name === "amount" || name === "bank_id") {
      setTimeout(() => {
        setBalanceErrors((prev) => {
          // After the setExpenseEntries state update is committed.
          const entry = (name === "amount")
            ? { ...expenseEntries[entryIndex], [name]: value } // simulate the update
            : expenseEntries[entryIndex];
          const amount = parseFloat(name === "amount" ? value : entry.amount) || 0;
          const bankId = name === "bank_id" ? value : entry.bank_id;
          const rawBalance = bankBalances[entryIndex]?.total_balance;
          const balance = rawBalance !== undefined && rawBalance !== null
            ? parseFloat(String(rawBalance).replace(/[^0-9.-]/g, ""))
            : null;

          if (bankId && amount > 0 && balance !== null && !isNaN(balance) && amount > balance) {
            return { ...prev, [entryIndex]: "Expense amount exceeds current bank balance!" };
          } else {
            const newErrors = { ...prev };
            delete newErrors[entryIndex];
            return newErrors;
          }
        });
      }, 0);
    }
  };

  const handleDateChange = (e, entryIndex) => {
    setExpenseEntries((prev) =>
      prev.map((entry, index) =>
        index === entryIndex ? { ...entry, date: e.value } : entry
      )
    );
  };

  const handleSelectChange = (selectedOption, fieldName, entryIndex) => {
    setExpenseEntries((prev) =>
      prev.map((entry, index) =>
        index === entryIndex ? { ...entry, [fieldName]: selectedOption.value } : entry
      )
    );

    // Fetch bank balance when bank is selected
    if (fieldName === "bank_id") {
      fetchBankBalance(selectedOption.value, entryIndex);
    }
  };

  const formSubmit = () => {
    // Validate that at least one complete entry exists
    const validEntries = expenseEntries.filter(
      (entry) =>
        entry.expense_category_id &&
        entry.amount &&
        entry.bank_id &&
        entry.date
    );

    if (validEntries.length === 0) {
      setError({ general: ["Please fill in at least one complete expense entry"] });
      return;
    }

    // Prevent submission if any entry exceeds current bank balance
    let hasBalanceViolation = false;
    const newBalanceErrors = { ...balanceErrors };

    expenseEntries.forEach((entry, entryIndex) => {
      const amount = parseFloat(entry.amount || 0) || 0;
      const rawBalance = bankBalances[entryIndex]?.total_balance;
      const balance = rawBalance !== undefined && rawBalance !== null
        ? parseFloat(String(rawBalance).replace(/[^0-9.-]/g, ""))
        : null;

      if (entry.bank_id && amount > 0 && balance !== null && !isNaN(balance) && amount > balance) {
        newBalanceErrors[entryIndex] = "Expense amount exceeds current bank balance!";
        hasBalanceViolation = true;
      } else {
        delete newBalanceErrors[entryIndex];
      }
    });

    if (hasBalanceViolation) {
      setBalanceErrors(newBalanceErrors);
      setError({ general: ["Please correct the highlighted amount(s) before submitting."] });
      return; // Block submission
    } else {
      setBalanceErrors(newBalanceErrors);
      setError({});
    }

    // Format dates to YYYY-MM-DD format for backend
    // const formattedEntries = validEntries.map(entry => ({
    //   ...entry,
    //   date: entry.date instanceof Date 
    //     ? entry.date.toISOString().split('T')[0] 
    //     : entry.date
    // }));

    const formattedEntries = validEntries.map(entry => {
      let formattedDate = entry.date;
      if (entry.date instanceof Date) {
        const year = entry.date.getFullYear();
        const month = String(entry.date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const day = String(entry.date.getDate()).padStart(2, '0');
        formattedDate = `${year}-${month}-${day}`;
      }
      return {
        ...entry,
        date: formattedDate
      };
    });

    // Prepare data for submission - backend expects direct array for multiple entries
    const submissionData = formattedEntries.length === 1 
      ? formattedEntries[0] 
      : formattedEntries;

    Http.post("/accounts/expenses", submissionData)
      .then((res) => {
        const message = res.data.message || 
          (formattedEntries.length === 1 
            ? "Expense created successfully" 
            : `${formattedEntries.length} expenses created successfully`);
        
        navigate("/expenses", {
          state: { message, type: "success" },
        });
      })
      .catch(function (e) {
        setError(e.response?.data?.errors || { general: ["An error occurred"] });
      });
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <LoaderComponent />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Create Expense</h5>
        </div>
        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            <h6 className="card-body-title mg-sm-b-30">Expense Information</h6>
            <div className="row">
              <div className="col-lg">
                <FormValidation error={error} />
              </div>
            </div>

            {expenseEntries.map((entry, entryIndex) => (
              <div key={entryIndex} className="expense-entry-form mb-4">
                {expenseEntries.length > 1 && (
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="text-primary mb-0">
                      <i className="fa fa-receipt me-2"></i>
                      Expense Entry #{entryIndex + 1}
                    </h6>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeExpenseEntry(entryIndex)}
                      title="Remove this expense entry"
                    >
                      <i className="fa fa-trash me-1"></i>
                      Remove
                    </button>
                  </div>
                )}

                <div className="row">
                  <div className="col-lg">
                    <label>
                      Date <span className="text-danger">*</span>
                    </label>
                    <Calendar
                      value={entry.date}
                      onChange={(e) => handleDateChange(e, entryIndex)}
                      dateFormat="dd/mm/yy"
                      className="w-100"
                    />
                  </div>
                  <div className="col-lg">
                    <label>
                      Category <span className="text-danger">*</span>
                    </label>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      name="expense_category_id"
                      value={
                        categories
                          .map((category) => ({
                            value: category.id,
                            label: `${category.name}`,
                          }))
                          .find(
                            (option) => option.value === entry.expense_category_id
                          ) || null
                      }
                      options={categories.map((category) => ({
                        value: category.id,
                        label: `${category.name}`,
                      }))}
                      onChange={(selectedOption) =>
                        handleSelectChange(selectedOption, "expense_category_id", entryIndex)
                      }
                    />
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-lg">
                    <label>
                      Amount <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="amount"
                      value={entry.amount || ""}
                      onChange={(e) => handleChange(e, entryIndex)}
                      placeholder="Enter Amount"
                    />
                    {balanceErrors[entryIndex] && (
                      <div className="text-danger mt-1">
                        <i className="fa fa-exclamation-circle me-1"></i>
                        {balanceErrors[entryIndex]}
                      </div>
                    )}
                  </div>
                  <div className="col-lg">
                    <label>
                      Bank <span className="text-danger">*</span>
                    </label>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      name="bank_id"
                      value={
                        banks
                          .map((bank) => ({
                            value: bank.id,
                            label: `${bank.name} - ${bank.account_number}`,
                          }))
                          .find((option) => option.value === entry.bank_id) || null
                      }
                      options={banks.map((bank) => ({
                        value: bank.id,
                        label: `${bank.name} - ${bank.account_number}`,
                      }))}
                      onChange={(selectedOption) =>
                        handleSelectChange(selectedOption, "bank_id", entryIndex)
                      }
                    />
                  </div>
                </div>

                {/* Bank Balance Display */}
                {entry.bank_id && (
                  <div className="row mt-2">
                    <div className="col-lg-6">
                      <div
                        className="alert alert-info"
                        style={{ marginBottom: "15px" }}
                      >
                        <strong>Bank Balance Information:</strong>
                        {loadingBalances[entryIndex] ? (
                          <div className="d-flex align-items-center mt-2">
                            <div
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                            <span>Loading bank balance...</span>
                          </div>
                        ) : balanceErrors[entryIndex] ? (
                          <div className="text-danger mt-2">
                            <i className="fa fa-exclamation-triangle me-1"></i>
                            {balanceErrors[entryIndex]}
                          </div>
                        ) : bankBalances[entryIndex] ? (
                          <div className="mt-2">
                            <div className="row">
                              <div className="col-md-4">
                                <small className="text-muted">Bank Name:</small>
                                <div className="font-weight-bold">
                                  {bankBalances[entryIndex].name}
                                </div>
                              </div>
                              <div className="col-md-4">
                                <small className="text-muted">
                                  Account Number:
                                </small>
                                <div className="font-weight-bold">
                                  {bankBalances[entryIndex].account_number}
                                </div>
                              </div>
                              <div className="col-md-4">
                                <small className="text-muted">
                                  Current Balance:
                                </small>
                                <div className="font-weight-bold text-success">
                                  ৳ {bankBalances[entryIndex].total_balance}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )}

                <div className="row mt-3">
                  <div className="col-lg">
                    <label>Reference</label>
                    <input
                      type="text"
                      className="form-control"
                      name="reference"
                      value={entry.reference || ""}
                      onChange={(e) => handleChange(e, entryIndex)}
                      placeholder="Enter Reference"
                    />
                  </div>

                  <div className="col-lg">
                    <label>Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={entry.description || ""}
                      onChange={(e) => handleChange(e, entryIndex)}
                      placeholder="Enter Description"
                      rows="3"
                    ></textarea>
                  </div>
                </div>

                {entryIndex < expenseEntries.length - 1 && (
                  <hr className="my-4" style={{ borderColor: "#dee2e6" }} />
                )}
              </div>
            ))}

            {/* Add More Button */}
            <div className="row mt-3">
              <div className="col-12">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={addExpenseEntry}
                >
                  <i className="fa fa-plus me-2"></i>
                  Add More Expense Entry
                </button>
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-3">
                <button
                  className="btn btn-primary btn-block mg-b-10"
                  onClick={formSubmit}
                >
                  <i className="fa fa-save mg-r-10"></i>
                  {expenseEntries.length > 1 ? `Save ${expenseEntries.length} Expenses` : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseCreate;
